import { DeleteOutlined } from "@ant-design/icons";
import { Button, Checkbox, Col, Flex, Input, InputNumber, Row, Select, Typography } from "antd";
import { useGetConnectionSequenceStepsQuery } from "api/TenantSlug/ConnectionSequenceStep/hooks";
import { FormFieldOverrideButton } from "components/Forms/FormFieldsForm/FormFieldOverrideButton";
import { FormFieldOverrideContext } from "components/Forms/FormFieldsForm/formFieldOverrides";
import { useFormFieldOverrides } from "components/Forms/FormFieldsForm/useFormFieldOverrides";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { FormFieldTypeSelectSearchable } from "components/UI/Selects/FormFieldTypeSelectSearchable/FormFieldTypeSelectSearchable";
import { StructureAutocompleteScenarioSelectSearchable } from "components/UI/Selects/StructureAutocompleteScenarioSelectSearchable/StructureAutocompleteScenarioSelectSearchable.tsx";
import { StructureModifyScenarioSelectSearchable } from "components/UI/Selects/StructureModifyScenarioSelectSearchable/StructureModifyScenarioSelectSearchable.tsx";
import { StructureValidateScenarioSelectSearchable } from "components/UI/Selects/StructureValidateScenarioSelectSearchable/StructureValidateScenarioSelectSearchable.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, ReactElement } from "react";
import { Control, FieldValues, useFieldArray, useWatch } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";
import { FormFieldOverride, FormFieldTypes } from "types/enums";
import { handleShowSearchFilterOption } from "utils/handleShowSearchFilterOption";

import ConnectionSequenceStepData = App.Data.ConnectionSequenceStepData;

interface FormFieldSettingsFieldsProps {
    type: FormFieldTypes;
    propertyPath: string;
    formStructureId?: string;
    overrideContext?: FormFieldOverrideContext;
}

interface OverrideableFormItemProps {
    control: Control<FieldValues>;
    name: string;
    label?: string;
    valuePropName?: string;
    overrideAvailable: boolean;
    overridden: boolean;
    onToggle: () => void;
    prefixPlacement?: "control" | "overlay";
    children: (disabled: boolean, prefix?: ReactElement) => ReactElement;
}

const OverrideableFormItem: FC<OverrideableFormItemProps> = ({
    control,
    name,
    label,
    valuePropName,
    overrideAvailable,
    overridden,
    onToggle,
    prefixPlacement = "control",
    children,
}) => {
    const inherited = overrideAvailable && !overridden;
    const overrideButton = overrideAvailable ? (
        <FormFieldOverrideButton overridden={overridden} onToggle={onToggle} />
    ) : undefined;
    const usesOverlay = overrideAvailable && prefixPlacement === "overlay";

    return (
        <div
            className={
                usesOverlay
                    ? "relative min-w-0 [&_.ant-checkbox-wrapper]:!ml-8 [&_.ant-input]:!pl-10 [&_.ant-input-number-input]:!pl-10 [&_.ant-select-selector]:!pl-10"
                    : "min-w-0"
            }
        >
            <FormItem
                control={control}
                name={name}
                label={label}
                valuePropName={valuePropName}
                layout="vertical"
                colon={false}
            >
                {children(inherited, prefixPlacement === "control" ? overrideButton : undefined)}
            </FormItem>
            {usesOverlay && (
                <div className={`absolute left-1 z-10 ${label ? "top-[34px]" : "top-1"}`}>{overrideButton}</div>
            )}
        </div>
    );
};

export const FormFieldSettingsFields: FC<FormFieldSettingsFieldsProps> = ({
    type,
    propertyPath,
    formStructureId,
    overrideContext,
}) => {
    const { t: tF } = usePaldockTranslation("forms.form-field-settings");
    const { t: tG } = usePaldockTranslation("forms.general-fields");
    const { t: tC } = usePaldockTranslation("common");
    const { control, setValue } = useGeneralForm();
    const isStructureContext = !!formStructureId;
    const watchedType = useWatch({ control, name: `${propertyPath}.type` }) as FormFieldTypes | undefined;
    const currentType = watchedType ?? type;
    const { isSourceLinked, isOverrideAvailable, isOverridden, toggleOverride } = useFormFieldOverrides(
        propertyPath,
        overrideContext
    );

    const overrideItemProps = (override: FormFieldOverride) => ({
        overrideAvailable: isOverrideAvailable(override),
        overridden: isOverridden(override),
        onToggle: () => toggleOverride(override),
    });

    const queryString = `meanings,
    requestConfig.responseHandlers.responseConnectionPropertiesCheck,
    requestConfig.requestConnectionPropertiesUrl,
    requestConfig.requestConnectionPropertiesUrl.requestConnectionPropertyModify,
    requestConfig.requestConnectionProperties,
    requestConfig.requestConnectionProperties.requestConnectionPropertyModify,
    requestConfig.headers,
    requestConfig.headers.requestConnectionPropertyModify,
    requestConfig.responseHandlers.stepConnection,
    requestConfig.responseHandlers.responseConnectionProperties,
    requestConfig.responseHandlers.responseConnectionProperties.requestConnectionPropertyModify`;

    const getConnectionSequenceStepsQuery = useGetConnectionSequenceStepsQuery(
        {
            filter: {
                include: queryString.replace(/\s+/g, ""),
                filter: { form_structure_id: formStructureId },
            },
        },
        { enabled: isStructureContext }
    );

    const connectionSteps = getConnectionSequenceStepsQuery.data?.connectionSequenceSteps;
    const connectionStepsOptions = connectionSteps?.map((step) => ({ label: step.name, value: step.id || "" })) || [];
    const usesRadioValues = currentType === FormFieldTypes.Radio || currentType === FormFieldTypes.MultiRadio;
    const usesSelectItems = currentType === FormFieldTypes.Select || currentType === FormFieldTypes.MultiSelect;
    const optionsOverride = usesRadioValues
        ? FormFieldOverride.FormFieldRadioValues
        : FormFieldOverride.FormFieldSelectItems;
    const optionsProperty = usesRadioValues ? "formFieldRadioValues" : "formFieldSelectItems";
    const optionsPath = `${propertyPath}.${optionsProperty}`;
    const { fields, append, remove } = useFieldArray({ control, name: optionsPath });
    const optionsInherited = isOverrideAvailable(optionsOverride) && !isOverridden(optionsOverride);
    const rangeInherited = isOverrideAvailable(FormFieldOverride.Range) && !isOverridden(FormFieldOverride.Range);

    const extractSystemNames = (step: ConnectionSequenceStepData): string => {
        const systemNames: string[] = [];

        step.requestConfig?.responseHandlers?.forEach((handler) => {
            handler?.responseConnectionProperties?.forEach((property) => {
                if (property) systemNames.push(property.system_name);
            });
        });

        return systemNames.join(",");
    };

    const handleSetAccessor = (accessorField: string, stepData: ConnectionSequenceStepData | null) => {
        setValue(accessorField, stepData ? extractSystemNames(stepData) || null : null);
    };

    return (
        <div className="rounded-lg border border-dashed bg-gray-100">
            <div className="m-3 grid grid-cols-3 gap-x-4 rounded-lg bg-white p-4">
                <Typography.Title level={4} className="col-span-3 my-3">
                    {tC("settings")}
                </Typography.Title>
                <OverrideableFormItem
                    control={control}
                    name={`${propertyPath}.system_name`}
                    label={tG("system_name.label")}
                    overrideAvailable={false}
                    overridden={false}
                    onToggle={() => undefined}
                >
                    {() => <Input disabled={isSourceLinked} />}
                </OverrideableFormItem>
                <OverrideableFormItem
                    control={control}
                    name={`${propertyPath}.type`}
                    label={tG("type.label")}
                    overrideAvailable={false}
                    overridden={false}
                    onToggle={() => undefined}
                >
                    {() => <FormFieldTypeSelectSearchable className="w-full" disabled={isSourceLinked} />}
                </OverrideableFormItem>
                <OverrideableFormItem
                    control={control}
                    name={`${propertyPath}.example_value`}
                    label={tF("fields.example_value.label")}
                    {...overrideItemProps(FormFieldOverride.ExampleValue)}
                >
                    {(disabled, prefix) => <Input disabled={disabled} prefix={prefix} />}
                </OverrideableFormItem>
                <OverrideableFormItem
                    control={control}
                    name={`${propertyPath}.placeholder`}
                    label={tF("fields.placeholder.label")}
                    {...overrideItemProps(FormFieldOverride.Placeholder)}
                >
                    {(disabled, prefix) => <Input disabled={disabled} prefix={prefix} />}
                </OverrideableFormItem>
                <OverrideableFormItem
                    control={control}
                    name={`${propertyPath}.default_value`}
                    label={tF("fields.default_value.label")}
                    {...overrideItemProps(FormFieldOverride.DefaultValue)}
                >
                    {(disabled, prefix) => <Input disabled={disabled} prefix={prefix} />}
                </OverrideableFormItem>
                <div aria-hidden />
                <div className="col-span-3 grid grid-cols-2 gap-x-4">
                    <OverrideableFormItem
                        control={control}
                        name={`${propertyPath}.not_show_if_system_name_equals`}
                        label={tF("fields.not_show_if_system_name_equals.label")}
                        {...overrideItemProps(FormFieldOverride.NotShowIfSystemNameEquals)}
                    >
                        {(disabled, prefix) => <Input disabled={disabled} prefix={prefix} />}
                    </OverrideableFormItem>
                    <OverrideableFormItem
                        control={control}
                        name={`${propertyPath}.show_only_if_system_name_equals`}
                        label={tF("fields.show_only_if_system_name_equals.label")}
                        {...overrideItemProps(FormFieldOverride.ShowOnlyIfSystemNameEquals)}
                    >
                        {(disabled, prefix) => <Input disabled={disabled} prefix={prefix} />}
                    </OverrideableFormItem>
                </div>
                <div className="col-span-3">
                    <OverrideableFormItem
                        control={control}
                        name={`${propertyPath}.doc_note`}
                        label={tF("fields.doc_note.label")}
                        {...overrideItemProps(FormFieldOverride.DocNote)}
                    >
                        {(disabled, prefix) => <Input disabled={disabled} prefix={prefix} />}
                    </OverrideableFormItem>
                </div>
                <div className="col-span-3 flex gap-x-3">
                    <OverrideableFormItem
                        control={control}
                        name={`${propertyPath}.is_required`}
                        valuePropName="checked"
                        prefixPlacement="overlay"
                        {...overrideItemProps(FormFieldOverride.IsRequired)}
                    >
                        {(disabled) => <Checkbox disabled={disabled}>{tF("fields.is_required.label")}</Checkbox>}
                    </OverrideableFormItem>
                    <OverrideableFormItem
                        control={control}
                        name={`${propertyPath}.is_hidden`}
                        valuePropName="checked"
                        prefixPlacement="overlay"
                        {...overrideItemProps(FormFieldOverride.IsHidden)}
                    >
                        {(disabled) => <Checkbox disabled={disabled}>{tF("fields.is_hidden.label")}</Checkbox>}
                    </OverrideableFormItem>
                </div>
            </div>

            <div className="m-3 rounded-lg bg-white p-4">
                <div className="my-3">
                    <Flex justify="space-between">
                        <Typography.Title level={4}>{tF("sections.validation.title")}</Typography.Title>
                    </Flex>
                </div>
                <div className="grid grid-cols-1">
                    <OverrideableFormItem
                        control={control}
                        name={`${propertyPath}.frontend_format`}
                        label={tF("fields.frontend_format.label")}
                        prefixPlacement="overlay"
                        {...overrideItemProps(FormFieldOverride.FrontendFormat)}
                    >
                        {(disabled) => <Input.TextArea disabled={disabled} />}
                    </OverrideableFormItem>
                    <OverrideableFormItem
                        control={control}
                        name={`${propertyPath}.validation_regex`}
                        label={tF("fields.validation_regex.label")}
                        prefixPlacement="overlay"
                        {...overrideItemProps(FormFieldOverride.ValidationRegex)}
                    >
                        {(disabled) => <Input.TextArea disabled={disabled} />}
                    </OverrideableFormItem>
                    {currentType === FormFieldTypes.Number && (
                        <div className="grid grid-cols-3 gap-x-4">
                            <OverrideableFormItem
                                control={control}
                                name={`${propertyPath}.min`}
                                label={tF("fields.min.label")}
                                {...overrideItemProps(FormFieldOverride.Range)}
                            >
                                {(disabled, prefix) => (
                                    <InputNumber className="w-full" disabled={disabled} prefix={prefix} />
                                )}
                            </OverrideableFormItem>
                            <OverrideableFormItem
                                control={control}
                                name={`${propertyPath}.max`}
                                label={tF("fields.max.label")}
                                overrideAvailable={false}
                                overridden={isOverridden(FormFieldOverride.Range)}
                                onToggle={() => undefined}
                            >
                                {() => <InputNumber className="w-full" disabled={rangeInherited} />}
                            </OverrideableFormItem>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-3 gap-x-4">
                    {isStructureContext && (
                        <>
                            <FormItem
                                control={control}
                                name={`${propertyPath}.validation_sequence_step_id`}
                                label={tF("fields.validation_sequence_step_id_old.label")}
                                layout="vertical"
                                colon={false}
                            >
                                <Select
                                    showSearch
                                    placeholder={tF("fields.validation_sequence_step_id_old.placeholder")}
                                    options={connectionStepsOptions}
                                    allowClear
                                    filterOption={handleShowSearchFilterOption}
                                />
                            </FormItem>
                            <FormItem
                                control={control}
                                name={`${propertyPath}.data_modification_sequence_step_id`}
                                label={tF("fields.data_modification_sequence_step_id_old.label")}
                                layout="vertical"
                                colon={false}
                            >
                                <Select
                                    showSearch
                                    placeholder={tF("fields.data_modification_sequence_step_id_old.placeholder")}
                                    options={connectionStepsOptions}
                                    allowClear
                                    filterOption={handleShowSearchFilterOption}
                                    onChange={(value) => {
                                        const step = connectionSteps?.find((item) => item.id === value) || null;
                                        handleSetAccessor(`${propertyPath}.data_modification_accessor`, step);
                                    }}
                                />
                            </FormItem>
                            <FormItem
                                control={control}
                                name={`${propertyPath}.autocomplete_sequence_step_id`}
                                label={tF("fields.autocomplete_sequence_step_id_old.label")}
                                layout="vertical"
                                colon={false}
                            >
                                <Select
                                    showSearch
                                    placeholder={tF("fields.autocomplete_sequence_step_id_old.placeholder")}
                                    options={connectionStepsOptions}
                                    allowClear
                                    filterOption={handleShowSearchFilterOption}
                                />
                            </FormItem>
                        </>
                    )}
                    <OverrideableFormItem
                        control={control}
                        name={`${propertyPath}.validation_scenarios`}
                        label={tF("fields.validation_scenarios_ids.label")}
                        prefixPlacement="overlay"
                        {...overrideItemProps(FormFieldOverride.ValidationScenarios)}
                    >
                        {(disabled) => (
                            <StructureValidateScenarioSelectSearchable
                                disabled={disabled}
                                formStructureId={formStructureId}
                                placeholder={tF("fields.validation_scenarios_ids.placeholder")}
                            />
                        )}
                    </OverrideableFormItem>
                    <OverrideableFormItem
                        control={control}
                        name={`${propertyPath}.modification_scenarios`}
                        label={tF("fields.modification_scenarios_ids.label")}
                        prefixPlacement="overlay"
                        {...overrideItemProps(FormFieldOverride.ModificationScenarios)}
                    >
                        {(disabled) => (
                            <StructureModifyScenarioSelectSearchable
                                disabled={disabled}
                                formStructureId={formStructureId}
                                placeholder={tF("fields.modification_scenarios_ids.placeholder")}
                            />
                        )}
                    </OverrideableFormItem>
                    <OverrideableFormItem
                        control={control}
                        name={`${propertyPath}.autocomplete_scenarios`}
                        label={tF("fields.autocomplete_scenarios_ids.label")}
                        prefixPlacement="overlay"
                        {...overrideItemProps(FormFieldOverride.AutocompleteScenarios)}
                    >
                        {(disabled) => (
                            <StructureAutocompleteScenarioSelectSearchable
                                disabled={disabled}
                                formStructureId={formStructureId}
                                placeholder={tF("fields.autocomplete_scenarios_ids.placeholder")}
                            />
                        )}
                    </OverrideableFormItem>
                    <OverrideableFormItem
                        control={control}
                        name={`${propertyPath}.data_modification_accessor`}
                        label={tF("fields.data_modification_accessor.label")}
                        {...overrideItemProps(FormFieldOverride.DataModificationAccessor)}
                    >
                        {(disabled, prefix) => <Input disabled={disabled} prefix={prefix} />}
                    </OverrideableFormItem>
                    <OverrideableFormItem
                        control={control}
                        name={`${propertyPath}.autocomplete_accessor`}
                        label={tF("fields.autocomplete_accessor.label")}
                        {...overrideItemProps(FormFieldOverride.AutocompleteAccessor)}
                    >
                        {(disabled, prefix) => <Input disabled={disabled} prefix={prefix} />}
                    </OverrideableFormItem>
                </div>
            </div>

            {(usesSelectItems || usesRadioValues) && (
                <div className="m-3 rounded-lg bg-white p-4">
                    <div className="flex items-center justify-between">
                        <Typography.Title level={5}>{tF("sections.select_options.title")}</Typography.Title>
                        {isOverrideAvailable(optionsOverride) && (
                            <FormFieldOverrideButton
                                overridden={isOverridden(optionsOverride)}
                                onToggle={() => toggleOverride(optionsOverride)}
                            />
                        )}
                    </div>
                    <Col span={24} className={optionsInherited ? "pointer-events-none opacity-60" : undefined}>
                        {fields.map((field, index) => (
                            <Row key={field.id} gutter={16} style={{ marginBottom: 16 }}>
                                <Col span={10}>
                                    <FormItem
                                        control={control}
                                        name={`${optionsPath}.${index}.value`}
                                        label={tF("fields.select_item_value.label")}
                                        required
                                        layout="vertical"
                                        colon={false}
                                    >
                                        <Input
                                            disabled={optionsInherited}
                                            placeholder={tF("fields.select_item_value.placeholder")}
                                        />
                                    </FormItem>
                                </Col>
                                <Col span={11}>
                                    <FormItem
                                        control={control}
                                        name={`${optionsPath}.${index}.visible_description`}
                                        label={tF("fields.select_item_description.label")}
                                        layout="vertical"
                                        colon={false}
                                    >
                                        <Input
                                            disabled={optionsInherited}
                                            placeholder={tF("fields.select_item_description.placeholder")}
                                        />
                                    </FormItem>
                                </Col>
                                <Col span={3} className="flex items-center">
                                    <Button
                                        disabled={optionsInherited}
                                        icon={<DeleteOutlined />}
                                        onClick={() => remove(index)}
                                    />
                                </Col>
                            </Row>
                        ))}
                        <Button
                            type="primary"
                            disabled={optionsInherited}
                            onClick={() => append({ id: null, value: "", visible_description: "" })}
                        >
                            {tF("actions.add_option")}
                        </Button>
                    </Col>
                </div>
            )}
        </div>
    );
};
